import type { CSSProperties } from 'react';
import type { VenueChoice, VenueId } from '@/api/types';
import type { BoothThemeId } from '@/features/gifts/fx';

/**
 * v9 venues (SPEC_V9 §B1, contract K2): where a Popin date "takes place".
 * A venue is a mood rendered on each device: a flat poster mark, a
 * decorative frame around the call stage (edges only, never over faces, no
 * background segmentation), a procedural ambience recipe and a default
 * photo-strip theme. Ids mirror backend/internal/venues (append-only).
 *
 * v11 "Poster" (docs/DESIGN.md): no gradient skins or glows. `accent` is the
 * venue mark's poster colour; text on it is `ink`.
 */
export type AmbienceRecipe = 'rain_kettle' | 'wind_city' | 'waves' | 'pages_rain' | 'crickets_fire' | 'fire_pad';

export interface VenueTheme {
  /** The venue mark's poster colour (#RRGGBB). */
  accent: string;
  /** Text colour that reads on the accent. */
  ink: string;
}

export interface Venue {
  id: VenueId;
  name: string;
  /** Short name for chips ("Chai tapri"). */
  short: string;
  /** "at the chai tapri" — inside a sentence. */
  place: string;
  line: string;
  emoji: string;
  theme: VenueTheme;
  ambience: AmbienceRecipe;
  /** What you'll hear, for the ambience control and the teaser. */
  sound: string;
  booth: BoothThemeId;
}

export const VENUES: readonly Venue[] = [
  {
    id: 'chai_tapri',
    name: 'Chai tapri in the monsoon',
    short: 'Chai tapri',
    place: 'the chai tapri',
    line: 'Rain on a tin roof, two cutting chais, nowhere to be.',
    emoji: '☕',
    theme: { accent: '#F47B2A', ink: '#1A1A1A' },
    ambience: 'rain_kettle',
    sound: 'Rain on the roof, a kettle on the boil',
    booth: 'chai',
  },
  {
    id: 'rooftop_night',
    name: 'Rooftop at night',
    short: 'Rooftop',
    place: 'the rooftop',
    line: 'Fairy lights, a warm breeze and the city humming below.',
    emoji: '🌃',
    theme: { accent: '#F2C21A', ink: '#1A1A1A' },
    ambience: 'wind_city',
    sound: 'A warm breeze, the city far below',
    booth: 'rooftop',
  },
  {
    id: 'beach_sunset',
    name: 'Beach at sunset',
    short: 'Beach',
    place: 'the beach',
    line: 'Barefoot, golden hour, waves keeping time.',
    emoji: '🌅',
    theme: { accent: '#ED4B44', ink: '#1A1A1A' },
    ambience: 'waves',
    sound: 'Waves rolling in, slow and steady',
    booth: 'sunset',
  },
  {
    id: 'bookstore_cafe',
    name: 'Bookstore café',
    short: 'Bookstore café',
    place: 'the bookstore café',
    line: 'Creaky shelves, rain on the glass, a corner table for two.',
    emoji: '📚',
    theme: { accent: '#18BC42', ink: '#1A1A1A' },
    ambience: 'pages_rain',
    sound: 'Quiet room, turning pages, rain on the glass',
    booth: 'bookstore',
  },
  {
    id: 'stargazing',
    name: 'Stargazing camp',
    short: 'Stargazing',
    place: 'the stargazing camp',
    line: 'A campfire, a blanket and more stars than you can count.',
    emoji: '✨',
    theme: { accent: '#2F6BFF', ink: '#1A1A1A' },
    ambience: 'crickets_fire',
    sound: 'Crickets and a crackling campfire',
    booth: 'starry',
  },
  {
    id: 'candlelight',
    name: 'Candlelight dinner',
    short: 'Candlelight',
    place: 'the candlelight dinner',
    line: 'Soft candlelight, a quiet table, all the time in the world.',
    emoji: '🕯️',
    theme: { accent: '#F28DB2', ink: '#1A1A1A' },
    ambience: 'fire_pad',
    sound: 'A soft fire and a warm hum',
    booth: 'candlelight',
  },
];

/** The default when the host picks none (K2). */
export const DEFAULT_VENUE: VenueId = 'chai_tapri';

/** v14: "No theme" — a plain date (no frame, no sound, no venue cards). */
export const NO_THEME = 'none' as const;
export const NO_THEME_LABEL = 'No theme';

/** The venue for an id ("No theme" and unknown ids read as none). */
export function venueOf(id: VenueChoice | null | undefined): Venue | null {
  return (id && VENUES.find((v) => v.id === id)) || null;
}

/** The venue's CSS custom properties (`--venue-*`) for its frame. */
export function venueVars(v: Venue): CSSProperties {
  return { '--venue-accent': v.theme.accent } as CSSProperties;
}

/**
 * Backdrop behind the desktop call stage. v11: the stage sits on the night
 * ground for every venue (the venue lives in its frame and its sound), so
 * this is always undefined; kept for callers.
 */
export function venueBackdrop(id: VenueId | null | undefined): CSSProperties | undefined {
  void id;
  return undefined;
}

/** The default photo-strip theme for a date at this venue. */
export function venueBoothTheme(id: VenueId | null | undefined): BoothThemeId | undefined {
  return venueOf(id)?.booth;
}
